import { z } from 'zod';
import { api } from '../lib/api';

// Import type enum
export const ImportTypeSchema = z.enum([
	'sinh-vien',
	'chuyen-doi-thpt',
	'top149',
	'province',
	'highschool',
	'faculty',
	'major',
	'admission-method'
]);
export type ImportType = z.infer<typeof ImportTypeSchema>;

// Import error schema
export const ImportErrorSchema = z.object({
	row: z.number(),
	column: z.string().optional(),
	message: z.string(),
	value: z.unknown().optional()
}).strip();

export type ImportError = z.infer<typeof ImportErrorSchema>;

// Màn xác nhận cập nhật: bản ghi nào sẽ bị ghi đè, và đổi những gì.
export const ThayDoiTruongSchema = z.object({
	truong: z.string(),
	nhan: z.string(),
	cu: z.string().nullable(),
	moi: z.string().nullable()
}).strip();

export const DongCapNhatSchema = z.object({
	khoa: z.string(),
	moTa: z.string().nullable(),
	thayDoi: z.array(ThayDoiTruongSchema)
}).strip();

export const KetQuaPhanLoaiSchema = z.object({
	soThemMoi: z.number(),
	soCapNhat: z.number(),
	dongCapNhat: z.array(DongCapNhatSchema),
	/** Bảng đã bị cắt vì quá dài — phần đuôi sẽ được ghi như cũ. */
	biCatBot: z.boolean()
}).strip();

export type ThayDoiTruong = z.infer<typeof ThayDoiTruongSchema>;
export type DongCapNhat = z.infer<typeof DongCapNhatSchema>;
export type KetQuaPhanLoai = z.infer<typeof KetQuaPhanLoaiSchema>;

// Validation result schema
export const ValidationResultSchema = z.object({
	success: z.boolean(),
	sessionId: z.string(),
	totalRows: z.number(),
	validRows: z.number(),
	errors: z.array(ImportErrorSchema),
	preview: z.array(z.record(z.string(), z.unknown())).optional(),
	// Cảnh báo tiếng Việt (vd nguy cơ RAM khi file lớn) — không chặn import.
	warnings: z.array(z.string()).optional(),
	// Số bí danh sẽ bị gỡ nếu bật "chấp nhận xoá bí danh cũ" (đổi mã THPT).
	soBiDanhSeChuyen: z.number().optional()
}).strip();

export type ValidationResult = z.infer<typeof ValidationResultSchema>;

// Skip reason schema for tracking why records were skipped
export const SkipReasonSchema = z.object({
	reason: z.string(),
	count: z.number()
}).strip();

export type SkipReason = z.infer<typeof SkipReasonSchema>;

// Import progress schema
export const ImportProgressSchema = z.object({
	status: z.enum(['starting', 'validating', 'importing', 'completed', 'error', 'cancelled']),
	currentRow: z.number(),
	totalRows: z.number(),
	percentage: z.number(),
	message: z.string().optional(),
	errors: z.array(ImportErrorSchema).optional(),
	// Result fields (present when completed)
	importedCount: z.number().optional(),
	updatedCount: z.number().optional(),
	skippedCount: z.number().optional(),
	skippedReasons: z.array(SkipReasonSchema).optional()
}).strip();

export type ImportProgress = z.infer<typeof ImportProgressSchema>;

// Import type labels in Vietnamese
export const ImportTypeLabels: Record<ImportType, string> = {
	'sinh-vien': 'Sinh viên',
	'chuyen-doi-thpt': 'Chuyển đổi mã THPT',
	'top149': 'Top 149',
	'province': 'Tỉnh thành',
	'highschool': 'Trường THPT',
	'faculty': 'Khoa',
	'major': 'Ngành',
	'admission-method': 'Phương thức tuyển sinh'
};

// Template filenames
export const TemplateFilenames: Record<ImportType, string> = {
	'sinh-vien': 'mau-sinh-vien.xlsx',
	'chuyen-doi-thpt': 'mau-chuyen-doi-thpt.xlsx',
	'top149': 'mau-top149.xlsx',
	'province': 'mau-tinh-thanh.xlsx',
	'highschool': 'mau-truong-thpt.xlsx',
	'faculty': 'mau-khoa.xlsx',
	'major': 'mau-nganh.xlsx',
	'admission-method': 'mau-phuong-thuc-tuyen-sinh.xlsx'
};

export const importApi = {
	/**
	 * Download template file
	 */
	downloadTemplate: async (type: ImportType): Promise<void> => {
		const response = await api.get(`/admin/import/templates/${type}`, {
			responseType: 'blob'
		});

		// Create download link
		const blob = new Blob([response.data], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		});
		const url = window.URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = TemplateFilenames[type];
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(url);
	},

	/**
	 * Validate uploaded file
	 */
	validateFile: async (type: ImportType, file: File): Promise<ValidationResult> => {
		const formData = new FormData();
		formData.append('file', file);

		const response = await api.post(`/admin/import/validate/${type}`, formData);

		return ValidationResultSchema.parse(response.data.data);
	},

	/**
	 * Create EventSource for import execution with SSE
	 */
	createExecuteStream: (
		type: ImportType,
		sessionId: string,
		onProgress: (progress: ImportProgress) => void,
		onComplete: (result: ImportProgress) => void,
		onError: (error: string) => void,
		chuyenBiDanhCu = false
	): EventSource => {
		const baseUrl = '/api';
		// Chỉ gửi tham số khi thật sự bật — server chỉ nhận đúng chuỗi 'true'.
		const url =
			`${baseUrl}/admin/import/execute/${type}?sessionId=${sessionId}` +
			(chuyenBiDanhCu ? '&chuyenBiDanhCu=true' : '');

		const eventSource = new EventSource(url, { withCredentials: true });

		eventSource.onmessage = (event) => {
			try {
				const data = JSON.parse(event.data);
				const progress = ImportProgressSchema.parse(data);

				if (progress.status === 'completed' || progress.status === 'error' || progress.status === 'cancelled') {
					onComplete(progress);
					eventSource.close();
				} else {
					onProgress(progress);
				}
			} catch (error) {
				console.error('Failed to parse SSE message:', error);
			}
		};

		eventSource.onerror = (error) => {
			console.error('SSE Error:', error);
			onError('Mất kết nối với máy chủ. Vui lòng thử lại.');
			eventSource.close();
		};

		eventSource.addEventListener('close', () => {
			eventSource.close();
		});

		return eventSource;
	},

	/**
	 * Cancel ongoing import
	 */
	/**
	 * Xem trước những bản ghi sẽ bị ghi đè nếu chạy phiên nhập này.
	 */
	previewUpdates: async (sessionId: string): Promise<KetQuaPhanLoai> => {
		const response = await api.get(`/admin/import/preview-updates/${sessionId}`);
		return KetQuaPhanLoaiSchema.parse(response.data.data);
	},

	/**
	 * Gửi danh sách khoá người dùng ĐỒNG Ý cho ghi đè. Gửi mảng rỗng nghĩa là
	 * không đồng ý ghi đè gì cả — dòng thêm mới vẫn được nhập.
	 */
	setUpdateSelection: async (sessionId: string, khoaDuocChon: string[]): Promise<void> => {
		await api.post(`/admin/import/update-selection/${sessionId}`, { khoaDuocChon });
	},

	cancelImport: async (sessionId: string): Promise<{ success: boolean; message: string }> => {
		const response = await api.post(`/admin/import/cancel/${sessionId}`);
		return response.data;
	},

	/**
	 * Get session status
	 */
	getSessionStatus: async (sessionId: string) => {
		const response = await api.get(`/admin/import/session/${sessionId}`);
		return response.data.data;
	}
};

// Table stats schema
export const TableStatsSchema = z.object({
	table: z.string(),
	displayName: z.string(),
	count: z.number()
}).strip();

export type TableStats = z.infer<typeof TableStatsSchema>;

// Delete result schema
export const DeleteResultSchema = z.object({
	success: z.boolean(),
	message: z.string(),
	deletedCounts: z.record(z.string(), z.number()).optional()
}).strip();

export type DeleteResult = z.infer<typeof DeleteResultSchema>;

export const dataManagementApi = {
	/**
	 * Get statistics for all deletable tables
	 */
	getTableStats: async (): Promise<TableStats[]> => {
		const response = await api.get('/admin/data/stats');
		return z.array(TableStatsSchema).parse(response.data.data);
	},

	/**
	 * Delete all records from a specific table (with cascading)
	 */
	deleteTable: async (tableName: string): Promise<DeleteResult> => {
		const response = await api.delete(`/admin/data/${tableName}`);
		return DeleteResultSchema.parse(response.data);
	},

	/**
	 * Delete all application data (requires confirmation)
	 */
	deleteAllData: async (confirmation: string): Promise<DeleteResult> => {
		const response = await api.delete('/admin/data/all', {
			data: { confirmation }
		});
		return DeleteResultSchema.parse(response.data);
	}
};
